import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Books, Plus, Play, FileText, Video, Trophy, CaretRight, Lock, ChatCircle, PaperPlaneRight, VideoCamera, Upload, UsersThree, ChartLineUp, Pencil, GearSix, Trash, PencilSimple, CheckCircle } from "@phosphor-icons/react";
import { Checkbox } from "../components/ui/checkbox";
import EmojiButton from "../components/EmojiButton";
import CourseSlides from "../components/CourseSlides";
import CourseFeedbackForm from "../components/CourseFeedbackForm";
import { toast } from "sonner";

function ProgressionRulesForm({ courseId, initial }) {
  const [mode, setMode] = useState(initial?.mode || "completion_only");
  const [score, setScore] = useState(initial?.passing_score || 60);
  const [lockdown, setLockdown] = useState(initial?.enforce_video_lockdown !== false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/courses/${courseId}/progression-rules`, { mode, passing_score: parseInt(score, 10) || 60, enforce_video_lockdown: lockdown });
      toast.success("Progression rules saved");
    } catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1"><input type="radio" name="mode" checked={mode === "completion_only"} onChange={() => setMode("completion_only")} data-testid="prog-mode-completion" /> Completion only</label>
        <label className="flex items-center gap-1"><input type="radio" name="mode" checked={mode === "passing_score"} onChange={() => setMode("passing_score")} data-testid="prog-mode-passing" /> Require passing score</label>
      </div>
      {mode === "passing_score" && (
        <div className="flex items-center gap-2 text-xs"><span>Min score</span><Input type="number" min={0} max={100} value={score} onChange={e => setScore(e.target.value)} className="rounded-none h-7 w-20" data-testid="prog-min-score" />%</div>
      )}
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={lockdown} onChange={e => setLockdown(e.target.checked)} data-testid="prog-video-lockdown" /> Enforce first-watch video lockdown</label>
      <Button size="sm" onClick={save} disabled={saving} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full text-xs" data-testid="save-progression-rules">{saving ? "Saving…" : "Save Rules"}</Button>
    </div>
  );
}

function PollsSection({ courseId, canEdit, userId }) {
  const [polls, setPolls] = useState([]);
  const [newPoll, setNewPoll] = useState({ question: "", options: ["", ""] });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get(`/courses/${courseId}/polls`).then(r => setPolls(r.data)).catch(() => {});
  }, [courseId]);

  const createPoll = async () => {
    if (!newPoll.question || newPoll.options.filter(o => o.trim()).length < 2) { toast.error("Need a question and at least 2 options"); return; }
    try {
      const { data } = await api.post(`/courses/${courseId}/polls`, { question: newPoll.question, options: newPoll.options.filter(o => o.trim()) });
      setPolls([data, ...polls]);
      setNewPoll({ question: "", options: ["", ""] });
      setCreating(false);
      toast.success("Poll created!");
    } catch { toast.error("Failed to create poll"); }
  };

  const votePoll = async (pollId, optIdx) => {
    try {
      const { data } = await api.post(`/polls/${pollId}/vote`, { option_index: optIdx });
      setPolls(prev => prev.map(p => p.id === pollId ? data : p));
    } catch { toast.error("Failed to vote"); }
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="mb-4">
          {!creating ? (
            <Button onClick={() => setCreating(true)} variant="outline" className="rounded-none gap-2 text-sm" data-testid="create-poll-btn"><Plus size={14} /> Create Poll</Button>
          ) : (
            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-5 space-y-3">
                <Input value={newPoll.question} onChange={e => setNewPoll({...newPoll, question: e.target.value})} placeholder="Poll question" className="rounded-none" data-testid="poll-question-input" />
                {newPoll.options.map((opt, i) => (
                  <Input key={i} value={opt} onChange={e => { const opts = [...newPoll.options]; opts[i] = e.target.value; setNewPoll({...newPoll, options: opts}); }} placeholder={`Option ${i + 1}`} className="rounded-none" />
                ))}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-none text-xs" onClick={() => setNewPoll({...newPoll, options: [...newPoll.options, ""]})}>Add Option</Button>
                  <Button size="sm" className="bg-blue-800 text-white rounded-none text-xs" onClick={createPoll} data-testid="save-poll-btn">Save Poll</Button>
                  <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => setCreating(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      {polls.length === 0 ? (
        <p className="text-sm text-slate-400">No polls yet</p>
      ) : polls.map(poll => {
        const totalVotes = Object.keys(poll.votes || {}).length;
        const myVote = poll.votes?.[userId];
        return (
          <Card key={poll.id} className="border border-slate-200 rounded-none shadow-sm" data-testid={`poll-${poll.id}`}>
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-slate-900 mb-3">{poll.question}</p>
              <div className="space-y-2">
                {poll.options?.map((opt, i) => {
                  const voteCount = Object.values(poll.votes || {}).filter(v => v === i).length;
                  const pct = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                  return (
                    <button key={i} onClick={() => votePoll(poll.id, i)} disabled={myVote !== undefined} className={`w-full text-left p-2 border text-sm transition-all ${myVote === i ? "border-blue-800 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex justify-between items-center">
                        <span className={myVote === i ? "font-semibold text-blue-800" : "text-slate-700"}>{opt}</span>
                        {myVote !== undefined && <span className="text-xs text-slate-500">{pct}% ({voteCount})</span>}
                      </div>
                      {myVote !== undefined && (
                        <div className="w-full h-1 bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-blue-800 transition-all" style={{ width: `${pct}%` }} /></div>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-2">{totalVotes} vote{totalVotes !== 1 ? "s" : ""}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function CourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Section M — Course Preview mode. Activated by ?preview=1 in the URL when
  // the viewer has course-management permission. Suppresses production writes
  // (progress, completion, certificates) so admins can experience the course
  // exactly as a student would.
  const previewMode = (typeof window !== "undefined") && new URLSearchParams(window.location.search).get("preview") === "1";
  const [course, setCourse] = useState(null);
  const [courseAccess, setCourseAccess] = useState(null);
  const [progress, setProgress] = useState(null);
  const [moduleAccess, setModuleAccess] = useState([]);
  const [moduleProgression, setModuleProgression] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleDialog, setModuleDialog] = useState(false);
  const [lessonDialog, setLessonDialog] = useState(false);
  const [liveClassDialog, setLiveClassDialog] = useState(false);
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [moduleForm, setModuleForm] = useState({ title: "", description: "", order: 0, forum_required: false, min_forum_posts: 1, discussion_topic: "" });
  const [lessonForm, setLessonForm] = useState({ title: "", content_type: "text", text_content: "", video_url: "", video_type: "", order: 0, require_video: false });
  const [liveClassForm, setLiveClassForm] = useState({ title: "", scheduled_at: "", duration: 60, meeting_link: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [liveClasses, setLiveClasses] = useState([]);
  const [activeTab, setActiveTab] = useState("content");
  const [courseEnrollees, setCourseEnrollees] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", status: "draft", is_free: false, programme_id: "", sequence_order: 0, release_enabled: false, release_date: "", release_offset_days: 0 });
  // Round 36 (#4) — configurable completion requirements (drag-drop reorder)
  const COMPLETION_OPTS = ["modules", "quizzes", "polls", "forum", "feedback"];
  const [completionReqs, setCompletionReqs] = useState(COMPLETION_OPTS);
  const [completionSaving, setCompletionSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showCompletionBadge, setShowCompletionBadge] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Check if the student has already submitted feedback for this course
  useEffect(() => {
    if (!id || !user || canEdit) return;
    api.get(`/courses/${id}/feedback/me`)
      .then(r => setFeedbackSubmitted(r.data?.submitted || false))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);
  // canEdit grants authoring permission. previewMode + canEdit means the
  // viewer is explicitly simulating the student experience and should be
  // treated as a student for gating / write-suppression purposes only.
  const canEdit = ["super_admin", "admin", "instructor"].includes(user?.role) && !previewMode;
  const closePreview = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("preview");
    window.location.href = url.pathname + (url.search ? "?" + url.search.replace(/^\?/, "") : "");
  };

  const [completedQuizIds, setCompletedQuizIds] = useState(new Set());

  const [accessError, setAccessError] = useState(null);

  const fetchCourse = () => {
    api.get(`/courses/${id}`).then(r => {
      setCourse(r.data);
      setLoading(false);
      setAccessError(null);
      // Section L — once we know the programme_id, persist mapping for resume.
      try {
        const pid = r.data?.programme_id;
        if (pid && typeof window !== "undefined") {
          const cur = JSON.parse(localStorage.getItem("sbl_last_course_per_programme") || "{}");
          delete cur.__pending_course_id__;
          cur[pid] = id;
          localStorage.setItem("sbl_last_course_per_programme", JSON.stringify(cur));
        }
      } catch (_e) { /* localStorage unavailable */ }
    }).catch((err) => {
      // Distinguish "course doesn't exist" (404) from "access denied" (403) so
      // students see a precise reason — not the generic "Course not found".
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (code === 403) {
        setAccessError({ code: 403, detail });
      } else if (code === 404) {
        setAccessError({ code: 404, detail: "This course does not exist or has been removed." });
      } else {
        setAccessError({ code: code || 0, detail: detail || "Could not load this course." });
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchCourse();
    // Section L — remember this course as the last-visited inside its
    // programme, so when the student returns to /programmes/{id} we can
    // scroll the relevant course card back into view.
    try {
      if (typeof window !== "undefined") {
        const cur = JSON.parse(localStorage.getItem("sbl_last_course_per_programme") || "{}");
        // We do not yet know the programme_id — set it after course load.
        cur.__pending_course_id__ = id;
        localStorage.setItem("sbl_last_course_per_programme", JSON.stringify(cur));
      }
    } catch (_e) { /* localStorage unavailable */ }
    api.get(`/progress/course/${id}`).then(r => setProgress(r.data)).catch(() => {});
    api.get(`/courses/${id}/module-access`).then(r => setModuleAccess(r.data)).catch(() => {});
    api.get(`/courses/${id}/module-progression`).then(r => setModuleProgression(r.data?.modules || [])).catch(() => {});
    api.get(`/courses/${id}/live-classes`).then(r => setLiveClasses(r.data)).catch(() => {});
    // Sequential course-level access check (hard-gates entire course UI).
    api.get(`/access/course/${id}`).then(r => setCourseAccess(r.data)).catch(() => setCourseAccess({ allowed: true, reason: "fallback" }));
    // Fetch quiz completion status
    api.get(`/courses/${id}/quiz-status`).then(r => {
      setCompletedQuizIds(new Set((r.data || []).filter(s => s.passed).map(s => s.quiz_id)));
    }).catch(() => {});
    if (canEdit) {
      api.get(`/courses/${id}/enrollees`).then(r => setCourseEnrollees(r.data)).catch(() => {});
      api.get("/programmes").then(r => setProgrammes(r.data)).catch(() => {});
    }
    loadChat();
  }, [id]);

  const loadChat = () => {
    api.get(`/courses/${id}/chat`).then(r => setChatMessages(r.data)).catch(() => {});
  };

  useEffect(() => {
    const interval = setInterval(loadChat, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const isModuleUnlocked = (moduleId) => {
    if (canEdit) return true;
    const access = moduleAccess.find(a => a.module_id === moduleId);
    return access ? access.unlocked : true;
  };

  const paymentPending = moduleAccess.some(a => a.payment_pending);

  // Course completion now requires THREE conditions:
  // 1) All modules complete
  // 2) All course quizzes passed
  // 3) Student has at least one forum / chat post in this course
  const myUid = user?.id || user?._id;
  const allModulesDone = moduleAccess.length > 0 && moduleAccess.every(a => a.completed);
  const allQuizzesPassed = !course?.quizzes || course.quizzes.length === 0
    ? true
    : course.quizzes.every(q => completedQuizIds.has(q.id));
  // Round 38 — align with the backend forum check which reads forum_threads
  // and forum_replies (not course_chat). The endpoint is authoritative; this
  // fallback keeps the UI in-sync while completion-status is loading.
  const [hasForumPost, setHasForumPost] = useState(false);
  useEffect(() => {
    if (!id || canEdit || !myUid) return;
    let cancelled = false;
    (async () => {
      try {
        const [threadsRes] = await Promise.all([
          api.get(`/courses/${id}/forum-threads`).catch(() => ({ data: [] })),
        ]);
        const threads = threadsRes.data || [];
        const uidStr = String(myUid);
        // Threads by the user OR any reply by the user across the threads.
        const authored = threads.some(t => String(t.author_id || t.user_id) === uidStr);
        if (authored) {
          if (!cancelled) setHasForumPost(true);
          return;
        }
        // Look at replies across the first ~20 threads (avoid a fan-out).
        const sample = threads.slice(0, 20);
        for (const t of sample) {
          const r = await api.get(`/forum-threads/${t.id}/replies`).catch(() => ({ data: [] }));
          if ((r.data || []).some(x => String(x.author_id || x.user_id) === uidStr)) {
            if (!cancelled) setHasForumPost(true);
            return;
          }
        }
        if (!cancelled) setHasForumPost(false);
      } catch (_) {
        if (!cancelled) setHasForumPost(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, canEdit, myUid]);
  // Round 36 — completion-status endpoint drives the next-step nudge AND
  // gates the badge. Falls back to legacy three-condition rule until the
  // endpoint responds.
  const [completionStatus, setCompletionStatus] = useState({ next_step: null, complete: false, percent: 0 });
  const refreshCompletionStatus = async () => {
    if (!id || canEdit) return;
    try {
      const { data } = await api.get(`/courses/${id}/completion-status`);
      setCompletionStatus(data);
    } catch (_) {}
  };
  useEffect(() => { refreshCompletionStatus(); /* eslint-disable-next-line */ }, [id, canEdit, allModulesDone, allQuizzesPassed, hasForumPost]);

  const courseCompleted =
    !canEdit && !paymentPending && !!completionStatus.complete;

  useEffect(() => {
    if (courseCompleted) {
      const shownKey = `sbl_badge_${id}`;
      // localStorage persists across sessions — dialog shows ONCE per user per course
      if (!localStorage.getItem(shownKey)) {
        setTimeout(() => setShowCompletionBadge(true), 500);
        localStorage.setItem(shownKey, "1");
      }
    }
  }, [courseCompleted, id]);

  const createModule = async () => {
    setSaving(true);
    try {
      await api.post(`/courses/${id}/modules`, { ...moduleForm, order: parseInt(moduleForm.order) || 0 });
      toast.success("Module created");
      setModuleDialog(false);
      setModuleForm({ title: "", description: "", order: 0 });
      fetchCourse();
    } catch (e) { toast.error("Failed to create module"); }
    setSaving(false);
  };

  const createLesson = async () => {
    setSaving(true);
    try {
      await api.post(`/modules/${activeModuleId}/lessons`, { ...lessonForm, order: parseInt(lessonForm.order) || 0 });
      toast.success("Lesson created");
      setLessonDialog(false);
      setLessonForm({ title: "", content_type: "text", text_content: "", video_url: "", video_type: "", order: 0, require_video: false });
      fetchCourse();
    } catch (e) { toast.error("Failed to create lesson"); }
    setSaving(false);
  };

  const createLiveClass = async () => {
    setSaving(true);
    try {
      await api.post("/live-classes", { ...liveClassForm, course_id: id, duration: parseInt(liveClassForm.duration) || 60 });
      toast.success("Live class scheduled");
      setLiveClassDialog(false);
      setLiveClassForm({ title: "", scheduled_at: "", duration: 60, meeting_link: "", description: "" });
      api.get(`/courses/${id}/live-classes`).then(r => setLiveClasses(r.data));
    } catch (e) { toast.error("Failed to schedule class"); }
    setSaving(false);
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    setSendingChat(true);
    try {
      await api.post(`/courses/${id}/chat`, { message: chatInput });
      setChatInput("");
      loadChat();
    } catch { toast.error("Failed to send"); }
    setSendingChat(false);
  };

  const handleFileUpload = async (e) => {
    // Round 36 — file upload replaced with Mail Assignment to learning@schoolbuilderlab.net.ng
    window.location.href = `mailto:learning@schoolbuilderlab.net.ng?subject=Assignment%20Submission%20-%20${encodeURIComponent(course?.title || id)}&body=${encodeURIComponent("Hi SBL team,%0D%0A%0D%0APlease find my assignment for this course attached.%0D%0A%0D%0AStudent: " + (user?.name || "") + "%0D%0ACourse: " + (course?.title || ""))}`;
    if (e?.preventDefault) e.preventDefault();
  };

  const openEditDialog = () => {
    setEditForm({
      title: course.title || "",
      description: course.description || "",
      status: course.status || "draft",
      is_free: course.is_free || false,
      programme_id: course.programme_id || "",
      sequence_order: course.sequence_order ?? course.order ?? 0,
      release_enabled: !!course.release_enabled,
      release_date: (course.release_date || "").slice(0, 10),
      release_offset_days: course.release_offset_days ?? 0,
    });
    // Round 36 (#4) — hydrate the drag-drop list with the course's saved order
    // (or fall back to the canonical default).
    const saved = Array.isArray(course.completion_requirements) && course.completion_requirements.length
      ? course.completion_requirements.filter(r => COMPLETION_OPTS.includes(r))
      : null;
    setCompletionReqs(saved || COMPLETION_OPTS);
    setEditDialog(true);
  };

  // Round 36 (#4) — drag handlers + save for the completion-requirements builder.
  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    setCompletionReqs(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(targetIdx, 0, moved);
      return next;
    });
    setDragIdx(null);
  };
  const toggleReq = (key) => {
    setCompletionReqs(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const saveCompletionReqs = async () => {
    setCompletionSaving(true);
    try {
      await api.put(`/courses/${id}/completion-requirements`, { requirements: completionReqs });
      toast.success("Completion requirements saved");
      fetchCourse();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setCompletionSaving(false);
  };

  const saveEditForm = async () => {
    setSaving(true);
    try {
      await api.put(`/courses/${id}`, {
        ...editForm,
        sequence_order: parseInt(editForm.sequence_order, 10) || 0,
        release_offset_days: parseInt(editForm.release_offset_days, 10) || 0,
      });
      toast.success("Course settings updated");
      setEditDialog(false);
      fetchCourse();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setSaving(false);
  };

  const handleDeleteCourse = async () => {
    if (!window.confirm("Are you sure you want to delete this course? This cannot be undone.")) return;
    try {
      await api.delete(`/courses/${id}`);
      toast.success("Course deleted");
      navigate("/courses");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete course"); }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module and all its lessons? This cannot be undone.")) return;
    try {
      await api.delete(`/modules/${moduleId}`);
      toast.success("Module deleted");
      fetchCourse();
    } catch (e) { toast.error("Failed to delete module"); }
  };

  const [editModuleDialog, setEditModuleDialog] = useState(false);
  const [editModuleForm, setEditModuleForm] = useState({ title: "", description: "", order: 0 });
  const [editModuleId, setEditModuleId] = useState(null);

  const openEditModule = (mod) => {
    setEditModuleId(mod.id);
    setEditModuleForm({ title: mod.title || "", description: mod.description || "", order: mod.order || 0 });
    setEditModuleDialog(true);
  };

  const saveEditModule = async () => {
    setSaving(true);
    try {
      await api.put(`/modules/${editModuleId}`, { ...editModuleForm, order: parseInt(editModuleForm.order) || 0 });
      toast.success("Module updated");
      setEditModuleDialog(false);
      fetchCourse();
    } catch (e) { toast.error("Failed to update module"); }
    setSaving(false);
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson? This cannot be undone.")) return;
    try {
      await api.delete(`/lessons/${lessonId}`);
      toast.success("Lesson deleted");
      fetchCourse();
    } catch (e) { toast.error("Failed to delete lesson"); }
  };

  const [editLessonDialog, setEditLessonDialog] = useState(false);
  const [editLessonForm, setEditLessonForm] = useState({ title: "", content_type: "text", text_content: "", video_url: "", order: 0, require_video: false });
  const [editLessonId, setEditLessonId] = useState(null);

  const openEditLesson = async (lesson) => {
    try {
      const { data } = await api.get(`/lessons/${lesson.id}`);
      setEditLessonId(lesson.id);
      setEditLessonForm({ title: data.title || "", content_type: data.content_type || "text", text_content: data.text_content || "", video_url: data.video_url || "", order: data.order || 0, require_video: !!data.require_video });
      setEditLessonDialog(true);
    } catch { toast.error("Failed to load lesson data"); }
  };

  const saveEditLesson = async () => {
    setSaving(true);
    try {
      await api.put(`/lessons/${editLessonId}`, { ...editLessonForm, order: parseInt(editLessonForm.order) || 0 });
      toast.success("Lesson updated");
      setEditLessonDialog(false);
      fetchCourse();
    } catch (e) { toast.error("Failed to update lesson"); }
    setSaving(false);
  };

  if (loading) return <Layout><div className="p-8"><div className="h-64 bg-slate-100 animate-pulse" /></div></Layout>;
  if (!course) {
    const ae = accessError;
    const isAccess = ae && ae.code === 403;
    return (
      <Layout>
        <div className="p-8 max-w-xl mx-auto text-center" data-testid="course-access-error">
          <div className="border border-slate-200 bg-white p-8">
            <p className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-2">
              {isAccess ? "Access not yet granted" : "Course unavailable"}
            </p>
            <h1 className="text-xl font-bold text-slate-950 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {isAccess ? "We couldn't open this course yet" : "Course not found"}
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              {ae?.detail || "This course is not available right now."}
            </p>
            <div className="mt-5 flex gap-2 justify-center">
              <Button onClick={() => navigate("/my-programmes")} className="rounded-none gap-2" data-testid="back-to-my-programmes">
                Back to My Programmes
              </Button>
              <Button variant="outline" onClick={() => navigate("/courses")} className="rounded-none" data-testid="back-to-my-learning">
                My Learning
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // HARD GATE — sequential course lock. Students cannot view a course until the
  // previous course in the programme is 100% complete.
  if (!canEdit && courseAccess && courseAccess.allowed === false && courseAccess.reason === "previous_course_incomplete") {
    return (
      <Layout>
        <div className="p-6 md:p-8 lg:p-12" data-testid="course-detail-page-locked">
          <Link to={course.programme_id ? `/programmes/${course.programme_id}` : "/courses"} className="text-sm text-blue-800 hover:underline mb-6 inline-block">&larr; Back to Programme</Link>
          <Card className="max-w-2xl border border-amber-300 rounded-none bg-amber-50/40 shadow-sm">
            <CardContent className="p-8 text-center">
              <Lock size={42} weight="fill" className="text-amber-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>This course is locked</h1>
              <p className="text-sm text-slate-700 mb-1">Course <strong>{courseAccess.course_index}</strong> of {courseAccess.course_total} in this programme.</p>
              <p className="text-sm text-slate-600 mb-6 max-w-md mx-auto">You must finish <strong>{courseAccess.prev_course_title || "the previous course"}</strong> (all lessons and all quizzes) before this one unlocks.</p>
              {courseAccess.prev_course_id && (
                <Link to={`/courses/${courseAccess.prev_course_id}`}>
                  <Button className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2" data-testid="go-prev-course-btn">
                    Resume previous course
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Round 42 · Task 2 — scheduled release lock.
  if (!canEdit && courseAccess && courseAccess.allowed === false && courseAccess.reason === "course_locked_by_schedule") {
    const releaseAt = courseAccess.release_at ? new Date(courseAccess.release_at) : null;
    const releaseText = releaseAt ? releaseAt.toLocaleString() : "a scheduled date";
    return (
      <Layout>
        <div className="p-6 md:p-8 lg:p-12" data-testid="course-detail-page-schedule-locked">
          <Link to={course.programme_id ? `/programmes/${course.programme_id}` : "/courses"} className="text-sm text-blue-800 hover:underline mb-6 inline-block">&larr; Back to Programme</Link>
          <Card className="max-w-2xl border border-blue-300 rounded-none bg-blue-50/40 shadow-sm">
            <CardContent className="p-8 text-center">
              <Lock size={42} weight="fill" className="text-blue-700 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>This course is scheduled to open later</h1>
              <p className="text-sm text-slate-700 mb-1">Unlocks on <strong>{releaseText}</strong>.</p>
              <p className="text-sm text-slate-600 max-w-md mx-auto">Please check back once the release window opens — your enrolment is confirmed.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="course-detail-page">
        {previewMode && (
          <div className="mb-4 flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 px-4 py-2" data-testid="course-preview-banner">
            <p className="text-xs sm:text-sm text-amber-900">
              <strong>Course Preview mode</strong> — experiencing this course exactly as a student would. Progress, quiz attempts and completion writes are <strong>suppressed</strong> in this session.
            </p>
            <Button onClick={closePreview} size="sm" variant="outline" className="rounded-none border-amber-300 text-amber-900 gap-2" data-testid="close-preview-btn">
              Close Preview
            </Button>
          </div>
        )}
        <Link to="/courses" className="text-sm text-blue-800 hover:underline mb-4 inline-block">&larr; Back to Courses</Link>

        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant={course.status === "published" ? "default" : "secondary"} className="rounded-none text-xs capitalize">{course.status}</Badge>
              {course.is_free && <Badge className="rounded-none text-xs bg-emerald-100 text-emerald-700 border-0">Free</Badge>}
              {!course.programme_id && <Badge variant="outline" className="rounded-none text-xs">Standalone</Badge>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{course.title}</h1>
            <p className="text-slate-600 mt-2 max-w-2xl">{course.description}</p>
            {course.instructor_name && <p className="text-sm text-slate-500 mt-1">Instructor: {course.instructor_name}</p>}
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <>
                <Button variant="outline" onClick={() => navigate(`/courses/${id}?preview=1`)} className="rounded-none gap-2 text-sm" data-testid="preview-course-btn" title="Experience the course as a student would, without writing to any records">
                  Preview Course
                </Button>
                <Button variant="outline" onClick={openEditDialog} className="rounded-none gap-2 text-sm" data-testid="edit-course-btn">
                  <GearSix size={16} /> Settings
                </Button>
                <Button variant="outline" onClick={handleDeleteCourse} className="rounded-none gap-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid="delete-course-btn">
                  <Trash size={16} /> Delete
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Edit Course Settings Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Course Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} className="mt-1 rounded-none" data-testid="edit-course-title" />
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="mt-1 rounded-none" rows={3} data-testid="edit-course-desc" />
              </div>
              <div>
                <Label className="text-sm">Programme</Label>
                <Select value={editForm.programme_id || "standalone"} onValueChange={v => setEditForm({...editForm, programme_id: v === "standalone" ? "" : v})}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="edit-course-programme"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standalone">Standalone (no programme)</SelectItem>
                    {programmes.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {editForm.programme_id && (
                <div>
                  <Label className="text-sm">Order # in programme</Label>
                  <Input type="number" min="0" value={editForm.sequence_order} onChange={e => setEditForm({...editForm, sequence_order: e.target.value})} className="mt-1 rounded-none" data-testid="edit-course-sequence-order" />
                  <p className="text-[11px] text-slate-500 mt-1">Course 1 is unlocked first; later courses unlock when the previous one is complete.</p>
                </div>
              )}
              <div className="flex items-center gap-3 p-3 border border-slate-200 bg-slate-50">
                <Checkbox id="edit_is_free" checked={editForm.is_free} onCheckedChange={v => setEditForm({...editForm, is_free: v})} data-testid="edit-course-free" />
                <div>
                  <Label htmlFor="edit_is_free" className="text-sm font-medium text-slate-700 cursor-pointer">Free Course</Label>
                  <p className="text-xs text-slate-500">Listed on the Free Courses page</p>
                </div>
              </div>
              <div>
                <Label className="text-sm">Status</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm({...editForm, status: v})}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Round 42 · Task 2 — Scheduled release */}
              <div className="border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="course-release-panel">
                <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editForm.release_enabled}
                    onChange={e => setEditForm({...editForm, release_enabled: e.target.checked})}
                    className="mt-0.5"
                    data-testid="edit-course-release-enabled"
                  />
                  <span><b>Delay this course</b> — lock until a fixed date OR N days after enrolment.</span>
                </label>
                {editForm.release_enabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    <div>
                      <Label className="text-xs text-slate-600">Fixed release date (wins if set)</Label>
                      <Input
                        type="date"
                        value={editForm.release_date}
                        onChange={e => setEditForm({...editForm, release_date: e.target.value})}
                        className="mt-1 rounded-none"
                        data-testid="edit-course-release-date"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">…or unlock N days after enrolment</Label>
                      <Input
                        type="number"
                        min="0"
                        value={editForm.release_offset_days}
                        onChange={e => setEditForm({...editForm, release_offset_days: e.target.value})}
                        className="mt-1 rounded-none"
                        placeholder="e.g. 7"
                        data-testid="edit-course-release-offset"
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 sm:col-span-2">
                      Programme students see a locked card with a countdown until the release moment. Staff always bypass.
                    </p>
                  </div>
                )}
              </div>

              {/* Round 36 (#4) — Drag to reorder course completion requirements */}
              <div className="border border-amber-200 bg-amber-50/40 p-3 space-y-2" data-testid="completion-requirements-builder">
                <div>
                  <Label className="text-sm font-semibold text-slate-900">Completion requirements</Label>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Drag to reorder. The first incomplete item becomes the student&apos;s next step on the course page.
                  </p>
                </div>
                <div className="space-y-1.5">
                  {completionReqs.map((req, idx) => (
                    <div
                      key={req}
                      draggable
                      onDragStart={() => onDragStart(idx)}
                      onDragOver={onDragOver}
                      onDrop={() => onDrop(idx)}
                      className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 cursor-move hover:border-blue-400"
                      data-testid={`completion-req-${req}`}
                    >
                      <span className="text-slate-400 text-xs select-none">⋮⋮</span>
                      <span className="text-xs font-mono text-slate-500 w-5">{idx + 1}.</span>
                      <span className="text-sm font-medium text-slate-900 capitalize flex-1">{req}</span>
                      <button
                        type="button"
                        onClick={() => toggleReq(req)}
                        className="text-[11px] text-red-600 hover:text-red-800"
                        data-testid={`completion-req-remove-${req}`}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {COMPLETION_OPTS.filter(o => !completionReqs.includes(o)).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <span className="text-[11px] text-slate-500">Add back:</span>
                      {COMPLETION_OPTS.filter(o => !completionReqs.includes(o)).map(o => (
                        <button
                          key={o}
                          type="button"
                          onClick={() => toggleReq(o)}
                          className="text-[11px] px-2 py-0.5 border border-slate-300 hover:bg-slate-100 capitalize"
                          data-testid={`completion-req-add-${o}`}
                        >
                          + {o}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={saveCompletionReqs}
                  disabled={completionSaving}
                  className="w-full mt-1 py-2 text-xs border border-amber-600 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                  data-testid="save-completion-requirements"
                >
                  {completionSaving ? "Saving…" : "Save completion order"}
                </button>
              </div>

              <Button onClick={saveEditForm} disabled={saving} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-course-settings">
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {progress && progress.total_lessons > 0 && (
          <Card className="border border-slate-200 rounded-none shadow-sm mb-6">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">Your Progress</span>
                  <span className="text-sm font-semibold text-slate-900">{progress.percentage}%</span>
                </div>
                <Progress value={progress.percentage} className="h-2" />
              </div>
              <span className="text-xs text-slate-500 whitespace-nowrap">{progress.completed_lessons}/{progress.total_lessons} lessons</span>
            </CardContent>
          </Card>
        )}

        {/* Tabs: Content | Chat | Live Classes */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="rounded-none bg-slate-100">
            <TabsTrigger value="content" className="rounded-none text-sm" data-testid="tab-content">Course Content</TabsTrigger>
            <TabsTrigger value="slides" className="rounded-none text-sm" data-testid="tab-slides">
              <FileText size={14} className="mr-1" /> Slides
            </TabsTrigger>
            <TabsTrigger value="chat" className="rounded-none text-sm" data-testid="tab-chat">
              <ChatCircle size={14} className="mr-1" /> Chat
            </TabsTrigger>
            <TabsTrigger value="live" className="rounded-none text-sm" data-testid="tab-live">
              <VideoCamera size={14} className="mr-1" /> Live Classes
            </TabsTrigger>
            {canEdit && (
              <TabsTrigger value="enrollees" className="rounded-none text-sm" data-testid="tab-enrollees">
                <UsersThree size={14} className="mr-1" /> Enrollees {courseEnrollees ? `(${courseEnrollees.total_enrollees})` : ""}
              </TabsTrigger>
            )}
            <TabsTrigger value="polls" className="rounded-none text-sm" data-testid="tab-polls">
              <ChartLineUp size={14} className="mr-1" /> Polls
            </TabsTrigger>
          </TabsList>

          {/* CONTENT TAB */}
          <TabsContent value="content">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Modules</h2>
                  {canEdit && (
                    <Dialog open={moduleDialog} onOpenChange={setModuleDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1" data-testid="add-module-btn"><Plus size={14} /> Add Module</Button>
                      </DialogTrigger>
                      <DialogContent className="rounded-none">
                        <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>New Module</DialogTitle></DialogHeader>
                        <div className="space-y-3 mt-2">
                          <div><Label className="text-sm">Title</Label><Input value={moduleForm.title} onChange={e => setModuleForm({...moduleForm, title: e.target.value})} className="mt-1 rounded-none" data-testid="module-title-input" /></div>
                          <div><Label className="text-sm">Description</Label><Textarea value={moduleForm.description} onChange={e => setModuleForm({...moduleForm, description: e.target.value})} className="mt-1 rounded-none" rows={2} /></div>
                          <div><Label className="text-sm">Order</Label><Input type="number" value={moduleForm.order} onChange={e => setModuleForm({...moduleForm, order: e.target.value})} className="mt-1 rounded-none" /></div>

                          {/* Section C — Discussion / forum participation requirement */}
                          <div className="border border-blue-200 bg-blue-50/30 p-3">
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={moduleForm.forum_required}
                                onChange={e => setModuleForm({...moduleForm, forum_required: e.target.checked})}
                                className="mt-0.5"
                                data-testid="module-forum-required"
                              />
                              <div className="text-sm">
                                <p className="font-semibold text-blue-900">Require forum participation to complete this module</p>
                                <p className="text-xs text-blue-800/80">Students must post at least <strong>N</strong> meaningful responses to the discussion question before the next module unlocks.</p>
                              </div>
                            </label>
                            {moduleForm.forum_required && (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                                <div className="sm:col-span-1">
                                  <Label className="text-xs">Minimum posts</Label>
                                  <Input type="number" min="1" value={moduleForm.min_forum_posts} onChange={e => setModuleForm({...moduleForm, min_forum_posts: parseInt(e.target.value || 1)})} className="rounded-none" data-testid="module-min-posts" />
                                </div>
                                <div className="sm:col-span-2">
                                  <Label className="text-xs">Discussion question / topic</Label>
                                  <Input value={moduleForm.discussion_topic} onChange={e => setModuleForm({...moduleForm, discussion_topic: e.target.value})} className="rounded-none" placeholder="e.g. Reflect on…" data-testid="module-discussion-topic" />
                                </div>
                                <div className="sm:col-span-3">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        // Suggestions require a module to exist first. If we're still creating
                                        // a new module, hit AI with the form context only.
                                        const { data } = await api.post(`/modules/${activeModuleId || "new"}/ai-suggest-discussion`, {
                                          extra_context: `${moduleForm.title}\n${moduleForm.description}`,
                                        });
                                        const first = (data.suggestions || [])[0];
                                        if (first) setModuleForm(m => ({ ...m, discussion_topic: first }));
                                      } catch (_e) { toast.error("AI suggestion unavailable"); }
                                    }}
                                    className="rounded-none gap-2 text-xs"
                                    data-testid="ai-suggest-discussion-btn"
                                  >
                                    AI suggest discussion question
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>

                          <Button onClick={createModule} disabled={saving || !moduleForm.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-module-btn">{saving ? "Saving..." : "Create Module"}</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>

                {paymentPending && !canEdit && (
                  <div className="bg-amber-50 border border-amber-200 p-4 mb-4 flex items-center gap-3" data-testid="payment-pending-banner">
                    <Lock size={20} className="text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Payment Required</p>
                      <p className="text-xs text-amber-700">Your payment is pending admin approval. Course content will be unlocked once payment is confirmed. <Link to="/payments" className="underline font-medium">Go to Payments</Link></p>
                    </div>
                  </div>
                )}

                {/* Round 36 — Next-step nudge driven by completion-status. */}
                {!canEdit && !paymentPending && completionStatus.next_step && (
                  <div className="bg-blue-50 border border-blue-300 p-4 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="completion-next-step-banner">
                    <div className="flex items-center gap-3 min-w-0">
                      <CheckCircle size={20} className="text-blue-700 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-blue-900">Next step: <span className="capitalize">{completionStatus.next_step.replace("_", " ")}</span></p>
                        <p className="text-xs text-blue-800">Completion steps: {completionStatus.percent}% — finish every required step to unlock your course completion badge.</p>
                      </div>
                    </div>
                    {completionStatus.next_step === "feedback" && (
                      <Button onClick={() => setFeedbackOpen(true)} size="sm" className="bg-blue-800 text-white hover:bg-blue-900 rounded-none">Give Feedback</Button>
                    )}
                  </div>
                )}

                {/* Persistent Feedback-Pending banner — shows whenever course is completed but feedback missing */}
                {!canEdit && courseCompleted && !feedbackSubmitted && (
                  <div className="bg-amber-50 border border-amber-300 p-4 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="feedback-pending-banner">
                    <div className="flex items-center gap-3 min-w-0">
                      <ChatCircle size={20} className="text-amber-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-amber-800">Course Feedback Pending</p>
                        <p className="text-xs text-amber-700">Your transcript can't show full marks until you complete the course feedback (10% of your score).</p>
                      </div>
                    </div>
                    <Button onClick={() => setFeedbackOpen(true)} size="sm" className="bg-amber-600 text-white hover:bg-amber-700 rounded-none" data-testid="open-feedback-banner">
                      Give Feedback Now
                    </Button>
                  </div>
                )}

                {/* Course Instructions */}
                {!canEdit && !paymentPending && (
                  <Card className="border border-blue-200 bg-blue-50/30 rounded-none shadow-sm mb-4" data-testid="course-instructions">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        <Books size={16} weight="fill" /> How to Complete This Course
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        {[
                          { step: "1", label: "Watch Video Lessons", desc: "Complete each lesson in the module sequentially", icon: Video },
                          { step: "2", label: "Join Discussion Forum", desc: "Engage with peers and instructors on course topics", icon: ChatCircle },
                          { step: "3", label: "Take the Quiz", desc: "Test your understanding with the module assessment", icon: Trophy },
                          { step: "4", label: "Earn Your Badge", desc: "Complete all modules to earn your course badge", icon: Trophy },
                        ].map((s, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-6 h-6 bg-blue-800 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{s.step}</div>
                            <div>
                              <p className="text-xs font-semibold text-slate-900">{s.label}</p>
                              <p className="text-[11px] text-slate-500">{s.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {(!course.modules || course.modules.length === 0) ? (
                  <Card className="border border-slate-200 rounded-none shadow-sm"><CardContent className="p-8 text-center"><Books size={40} className="text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No modules yet</p></CardContent></Card>
                ) : paymentPending && !canEdit ? (
                  <Card className="border border-amber-300 rounded-none shadow-sm bg-amber-50/40" data-testid="course-locked-card">
                    <CardContent className="p-8 text-center">
                      <Lock size={40} className="text-amber-600 mx-auto mb-3" weight="fill" />
                      <h3 className="text-base font-bold text-amber-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Course content is locked</h3>
                      <p className="text-xs text-amber-800 mb-4 max-w-md mx-auto">Settle at least 60% of your programme fee to unlock all modules, lessons, slides, quizzes, live meetings and your discussion forum.</p>
                      <Link to="/payments"><Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="locked-go-pay-btn">Go to Payments</Button></Link>
                    </CardContent>
                  </Card>
                ) : (
                  <Accordion type="multiple" className="space-y-2">
                    {course.modules.map((mod, mIdx) => {
                      const unlocked = isModuleUnlocked(mod.id);
                      const accessInfo = moduleAccess.find(a => a.module_id === mod.id);
                      const prog = moduleProgression.find(p => p.module_id === mod.id);
                      const progLocked = prog?.locked === true;
                      // Staff (super_admin / admin / instructor) bypass ALL module locks
                      // so they can author, edit, and add lessons / quizzes regardless of
                      // sequential or progression rules. Students still see the locks.
                      const finalUnlocked = canEdit ? true : (unlocked && !progLocked);
                      return (
                        <AccordionItem key={mod.id} value={mod.id} className={`border border-slate-200 rounded-none shadow-sm bg-white ${!finalUnlocked ? "opacity-70" : ""}`} disabled={!finalUnlocked}>
                          <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid={`module-${mIdx}`}>
                            <div className="flex items-center gap-3 text-left flex-1">
                              <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 text-sm font-bold ${finalUnlocked ? "bg-blue-800/10 text-blue-800" : "bg-slate-200 text-slate-400"}`}>
                                {finalUnlocked ? mIdx + 1 : <Lock size={14} />}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-semibold text-slate-900 text-sm">{mod.title}</p>
                                  {prog && (
                                    progLocked && !canEdit ? (
                                      <Badge className="rounded-none text-[9px] bg-slate-300 text-slate-700 border-0" data-testid={`mod-badge-locked-${mIdx}`}>Locked</Badge>
                                    ) : prog.lessons_complete && (prog.quiz_status === "passed" || prog.quiz_status === "no_quiz") ? (
                                      <Badge className="rounded-none text-[9px] bg-emerald-600 text-white border-0" data-testid={`mod-badge-complete-${mIdx}`}>Complete</Badge>
                                    ) : (
                                      <Badge className="rounded-none text-[9px] bg-blue-100 text-blue-800 border-0" data-testid={`mod-badge-progress-${mIdx}`}>In progress</Badge>
                                    )
                                  )}
                                  {prog?.has_quiz && (
                                    <Badge className={`rounded-none text-[9px] border-0 ${
                                      prog.quiz_status === "passed" ? "bg-emerald-100 text-emerald-800" :
                                      prog.quiz_status === "failed" ? "bg-red-100 text-red-700" :
                                      prog.quiz_status === "not_attempted" ? "bg-amber-100 text-amber-800" :
                                      "bg-slate-100 text-slate-600"
                                    }`} data-testid={`mod-quiz-status-${mIdx}`}>
                                      Quiz: {prog.quiz_status.replace("_", " ")}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500">
                                  {mod.lessons?.length || 0} lessons
                                  {progLocked && !canEdit && (prog?.lock_reason || " — Complete previous module to unlock")}
                                  {accessInfo && finalUnlocked && ` — ${accessInfo.completed_lessons}/${accessInfo.total_lessons} done`}
                                </p>
                              </div>
                              {canEdit && (
                                <div className="flex gap-1 mr-2" onClick={e => e.stopPropagation()}>
                                  <button onClick={() => openEditModule(mod)} className="p-1.5 hover:bg-blue-50 text-slate-500 hover:text-blue-700 transition-colors" data-testid={`edit-module-${mIdx}`}>
                                    <PencilSimple size={14} />
                                  </button>
                                  <button onClick={() => handleDeleteModule(mod.id)} className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-module-${mIdx}`}>
                                    <Trash size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-3">
                            {mod.description && <p className="text-sm text-slate-600 mb-3">{mod.description}</p>}
                            {finalUnlocked ? (
                              <div className="space-y-1">
                                {mod.lessons?.map((lesson, lIdx) => {
                                  const isCompleted = progress?.completed_lesson_ids?.includes(lesson.id);
                                  return (
                                    <div key={lesson.id} className="flex items-center gap-3 px-3 py-2 text-sm transition-all hover:bg-slate-50 group/lesson">
                                      <Link to={`/lessons/${lesson.id}`} data-testid={`lesson-${mIdx}-${lIdx}`}
                                        className={`flex items-center gap-3 flex-1 ${isCompleted ? "text-emerald-700" : "text-slate-700"}`}>
                                        {lesson.content_type === "video" ? <Video size={16} className="text-blue-800 flex-shrink-0" /> : <FileText size={16} className="text-slate-400 flex-shrink-0" />}
                                        <span className="flex-1">{lesson.title}</span>
                                        {isCompleted && <Badge className="rounded-none text-xs bg-emerald-100 text-emerald-700">Done</Badge>}
                                        <CaretRight size={14} className="text-slate-400" />
                                      </Link>
                                      {canEdit && (
                                        <div className="flex gap-0.5 opacity-0 group-hover/lesson:opacity-100 transition-opacity">
                                          <button onClick={() => openEditLesson(lesson)} className="p-1 hover:bg-blue-50 text-slate-400 hover:text-blue-700 transition-colors" data-testid={`edit-lesson-${mIdx}-${lIdx}`}>
                                            <PencilSimple size={12} />
                                          </button>
                                          <button onClick={() => handleDeleteLesson(lesson.id)} className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-lesson-${mIdx}-${lIdx}`}>
                                            <Trash size={12} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 italic">Complete all lessons in the previous module to access this content.</p>
                            )}
                            {canEdit && unlocked && (
                              <Button size="sm" variant="outline" className="mt-2 rounded-none text-xs gap-1" data-testid={`add-lesson-${mIdx}`}
                                onClick={(e) => { e.stopPropagation(); setActiveModuleId(mod.id); setLessonDialog(true); }}>
                                <Plus size={12} /> Add Lesson
                              </Button>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                <Card className="border border-slate-200 rounded-none shadow-sm">
                  <CardContent className="p-5">
                    <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      <Trophy size={18} className="text-gold-500" /> Quizzes & Assignments
                    </h3>
                    {course.quizzes && course.quizzes.length > 0 ? (
                      <div className="space-y-2">
                        {course.quizzes.map((quiz, qIdx) => (
                          <div key={quiz.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-sm group">
                            <Link to={`/quizzes/${quiz.id}`} className="flex items-center gap-2 flex-1 min-w-0" data-testid={`quiz-${qIdx}`}>
                              {completedQuizIds.has(quiz.id) ? (
                                <CheckCircle size={14} className="text-emerald-600 flex-shrink-0" weight="fill" />
                              ) : (
                                <Trophy size={14} className="text-gold-500 flex-shrink-0" />
                              )}
                              <span className={`truncate ${completedQuizIds.has(quiz.id) ? "text-emerald-700" : "text-slate-700"}`}>{quiz.title}</span>
                              {completedQuizIds.has(quiz.id) && <Badge className="rounded-none text-[9px] bg-emerald-100 text-emerald-700 border-0 ml-1">Passed</Badge>}
                              {quiz.module_id && <Badge className="rounded-none text-[9px] bg-blue-100 text-blue-800 border-0 ml-1">Module Quiz</Badge>}
                            </Link>
                            {canEdit && (
                              <div className="flex gap-1 items-center flex-shrink-0">
                                <select
                                  defaultValue={quiz.module_id || ""}
                                  onChange={async (e) => {
                                    const mid = e.target.value;
                                    if (!mid) return;
                                    try { await api.put(`/quizzes/${quiz.id}/module`, { module_id: mid }); toast.success("Attached to module"); fetchCourse(); }
                                    catch { toast.error("Failed"); }
                                  }}
                                  className="text-[11px] border border-slate-200 rounded-none h-7 px-1 opacity-0 group-hover:opacity-100"
                                  data-testid={`quiz-module-select-${qIdx}`}
                                >
                                  <option value="">— Attach to module —</option>
                                  {(course.modules || []).map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                                </select>
                                <Link to={`/quizzes/${quiz.id}/edit`} data-testid={`edit-quiz-${qIdx}`} className="opacity-0 group-hover:opacity-100">
                                  <button className="p-1 text-slate-400 hover:text-blue-700"><PencilSimple size={14} /></button>
                                </Link>
                                <button onClick={async () => { if (!window.confirm("Delete this quiz?")) return; try { await api.delete(`/quizzes/${quiz.id}`); toast.success("Quiz deleted"); fetchCourse(); } catch { toast.error("Failed"); } }} className="p-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100" data-testid={`delete-quiz-${qIdx}`}>
                                  <Trash size={14} />
                                </button>
                              </div>
                            )}
                            <CaretRight size={14} className="text-slate-400 flex-shrink-0" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No quizzes yet</p>
                    )}
                    {canEdit && (
                      <Link to={`/courses/${id}/create-quiz`}>
                        <Button size="sm" variant="outline" className="mt-3 w-full rounded-none gap-1 text-xs" data-testid="create-quiz-btn"><Plus size={12} /> Create Quiz/Assignment</Button>
                      </Link>
                    )}
                    {!canEdit && (
                      /* Round 37 — restore the Mail Assignment CTA that was previously
                         defined in handleFileUpload but never rendered. Students click
                         it to open their mail client with a pre-filled submission
                         addressed to learning@schoolbuilderlab.net.ng. */
                      <Button
                        size="sm"
                        onClick={handleFileUpload}
                        className="mt-3 w-full rounded-none gap-2 text-xs bg-blue-800 text-white hover:bg-blue-900"
                        data-testid="mail-assignment-btn"
                      >
                        <PaperPlaneRight size={12} /> Mail Assignment
                      </Button>
                    )}
                    {canEdit && (
                      <div className="mt-4 pt-3 border-t border-slate-200" data-testid="progression-rules-block">
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Progression Rules</p>
                        <ProgressionRulesForm courseId={id} initial={{ mode: course.progression_mode, passing_score: course.progression_passing_score, enforce_video_lockdown: course.enforce_video_lockdown }} />
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card className="border border-slate-200 rounded-none shadow-sm">
                  <CardContent className="p-5">
                    <h3 className="text-base font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Discussion Forum</h3>
                    <p className="text-sm text-slate-500 mb-3">Ask questions and discuss with peers</p>
                    <Link to={`/forum?course=${id}`}><Button variant="outline" size="sm" className="w-full rounded-none" data-testid="course-forum-btn">Go to Forum</Button></Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* SLIDES TAB */}
          <TabsContent value="slides">
            <CourseSlides courseId={id} currentUser={user} canUpload={canEdit} />
          </TabsContent>

          {/* CHAT TAB */}
          <TabsContent value="chat">
            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-0">
                <div className="h-[50vh] overflow-y-auto p-4 space-y-3">
                  {chatMessages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <ChatCircle size={40} className="mb-2" />
                      <p className="text-sm">No messages yet. Start the conversation!</p>
                    </div>
                  )}
                  {chatMessages.map((msg, idx) => (
                    <div key={msg.id || idx} className={`flex ${msg.user_id === (user?.id || user?._id) ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${idx}`}>
                      <div className={`max-w-[75%] px-3 py-2 ${msg.user_id === (user?.id || user?._id) ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-800 border border-slate-200"}`}>
                        <p className="text-xs font-medium mb-0.5 opacity-75">{msg.user_name} ({msg.user_role})</p>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className="text-[10px] opacity-50 mt-1">{new Date(msg.created_at).toLocaleTimeString()}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="border-t border-slate-200 p-3 flex gap-2 items-center">
                  <EmojiButton onSelect={(emoji) => setChatInput(prev => prev + emoji)} testId="course-chat-emoji" />
                  <Input value={chatInput} onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                    placeholder="Type a message..." className="rounded-none flex-1" data-testid="course-chat-input" />
                  <Button onClick={sendChatMessage} disabled={sendingChat || !chatInput.trim()} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="send-chat-btn">
                    <PaperPlaneRight size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LIVE CLASSES TAB */}
          <TabsContent value="live">
            <div className="space-y-4">
              {canEdit && (
                <Dialog open={liveClassDialog} onOpenChange={setLiveClassDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="schedule-live-class-btn">
                      <VideoCamera size={16} /> Schedule Live Class
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none">
                    <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Schedule Live Class</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-2">
                      <div><Label className="text-sm">Title</Label><Input value={liveClassForm.title} onChange={e => setLiveClassForm({...liveClassForm, title: e.target.value})} className="mt-1 rounded-none" data-testid="live-class-title" /></div>
                      <div><Label className="text-sm">Date & Time</Label><Input type="datetime-local" value={liveClassForm.scheduled_at} onChange={e => setLiveClassForm({...liveClassForm, scheduled_at: e.target.value})} className="mt-1 rounded-none" /></div>
                      <div><Label className="text-sm">Duration (minutes)</Label><Input type="number" value={liveClassForm.duration} onChange={e => setLiveClassForm({...liveClassForm, duration: e.target.value})} className="mt-1 rounded-none" /></div>
                      <div><Label className="text-sm">Meeting Link (Zoom/Google Meet)</Label><Input value={liveClassForm.meeting_link} onChange={e => setLiveClassForm({...liveClassForm, meeting_link: e.target.value})} className="mt-1 rounded-none" placeholder="https://meet.google.com/..." data-testid="live-class-link" /></div>
                      <div><Label className="text-sm">Description</Label><Textarea value={liveClassForm.description} onChange={e => setLiveClassForm({...liveClassForm, description: e.target.value})} className="mt-1 rounded-none" rows={2} /></div>
                      <Button onClick={createLiveClass} disabled={saving || !liveClassForm.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-live-class-btn">{saving ? "Scheduling..." : "Schedule Class"}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {liveClasses.length === 0 ? (
                <div className="text-center py-12"><VideoCamera size={40} className="text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No live classes scheduled</p></div>
              ) : (
                liveClasses.map((lc, idx) => (
                  <Card key={lc.id} className="border border-slate-200 rounded-none shadow-sm" data-testid={`live-class-${idx}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{lc.title}</h3>
                          <p className="text-sm text-slate-500 mt-1">{new Date(lc.scheduled_at).toLocaleString()} &middot; {lc.duration} min</p>
                          {lc.instructor_name && <p className="text-xs text-slate-500">Instructor: {lc.instructor_name}</p>}
                          {lc.description && <p className="text-sm text-slate-600 mt-2">{lc.description}</p>}
                        </div>
                        {lc.meeting_link && (
                          <a href={lc.meeting_link} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-gold-500 text-slate-950 hover:bg-gold-600 rounded-none gap-2" data-testid={`join-live-class-${idx}`}>
                              <VideoCamera size={16} /> Join Class
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ENROLLEES TAB */}
          {canEdit && (
            <TabsContent value="enrollees">
              {courseEnrollees ? (
                <div className="space-y-6" data-testid="course-enrollees-view">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card className="border border-slate-200 rounded-none shadow-sm">
                      <CardContent className="p-4 text-center">
                        <UsersThree size={24} className="text-blue-800 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Total Enrollees</p>
                        <p className="text-xl font-bold text-slate-900">{courseEnrollees.total_enrollees}</p>
                      </CardContent>
                    </Card>
                    <Card className="border border-slate-200 rounded-none shadow-sm">
                      <CardContent className="p-4 text-center">
                        <ChartLineUp size={24} className="text-emerald-600 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Group Avg Progress</p>
                        <p className="text-xl font-bold text-slate-900">{courseEnrollees.group_avg_progress}%</p>
                      </CardContent>
                    </Card>
                    <Card className="border border-slate-200 rounded-none shadow-sm">
                      <CardContent className="p-4 text-center">
                        <Books size={24} className="text-amber-500 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Total Lessons</p>
                        <p className="text-xl font-bold text-slate-900">{courseEnrollees.total_lessons}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <Card className="border border-slate-200 rounded-none shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Overall Group Progress</span>
                        <span className="text-sm font-bold text-slate-900">{courseEnrollees.group_avg_progress}%</span>
                      </div>
                      <Progress value={courseEnrollees.group_avg_progress} className="h-3" />
                    </CardContent>
                  </Card>
                  {courseEnrollees.enrollees.length > 0 ? (
                    <Card className="border border-slate-200 rounded-none shadow-sm overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs uppercase tracking-wider">Student</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider">Progress</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-right">Lessons</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {courseEnrollees.enrollees.map((e, idx) => (
                            <TableRow key={e.user_id} data-testid={`course-enrollee-${idx}`}>
                              <TableCell>
                                <p className="text-sm font-medium text-slate-900">{e.name}</p>
                                <p className="text-xs text-slate-500">{e.email}</p>
                              </TableCell>
                              <TableCell>
                                <Badge variant={e.status === "active" ? "default" : "secondary"} className="rounded-none text-xs capitalize">{e.status?.replace("_", " ")}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 min-w-[120px]">
                                  <Progress value={e.progress_percent} className="h-2 flex-1" />
                                  <span className="text-xs font-semibold text-slate-700 w-10 text-right">{e.progress_percent}%</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right text-xs text-slate-600">{e.completed_lessons}/{e.total_lessons}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Card>
                  ) : (
                    <div className="text-center py-12">
                      <UsersThree size={40} className="text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No enrollees yet</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center"><div className="h-32 bg-slate-100 animate-pulse" /></div>
              )}
            </TabsContent>
          )}

          {/* Polls Tab */}
          <TabsContent value="polls">
            <PollsSection courseId={id} canEdit={canEdit} userId={user?.id || user?._id} />
          </TabsContent>
        </Tabs>

        {/* Lesson Dialog */}
        <Dialog open={lessonDialog} onOpenChange={setLessonDialog}>
          <DialogContent className="sm:max-w-lg rounded-none">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>New Lesson</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-2">
              <div><Label className="text-sm">Title</Label><Input value={lessonForm.title} onChange={e => setLessonForm({...lessonForm, title: e.target.value})} className="mt-1 rounded-none" data-testid="lesson-title-input" /></div>
              <div>
                <Label className="text-sm">Content Type</Label>
                <Select value={lessonForm.content_type} onValueChange={v => setLessonForm({...lessonForm, content_type: v})}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="text">Text</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="mixed">Mixed</SelectItem></SelectContent>
                </Select>
              </div>
              {(lessonForm.content_type === "text" || lessonForm.content_type === "mixed") && (
                <div><Label className="text-sm">Text Content</Label><Textarea value={lessonForm.text_content} onChange={e => setLessonForm({...lessonForm, text_content: e.target.value})} className="mt-1 rounded-none" rows={5} /></div>
              )}
              {(lessonForm.content_type === "video" || lessonForm.content_type === "mixed") && (
                <>
                  <div><Label className="text-sm">Video URL (YouTube link or embed URL)</Label><Input value={lessonForm.video_url} onChange={e => setLessonForm({...lessonForm, video_url: e.target.value})} className="mt-1 rounded-none" placeholder="https://www.youtube.com/watch?v=... or embed URL" /></div>
                  <div>
                    <Label className="text-sm">Video Type</Label>
                    <Select value={lessonForm.video_type} onValueChange={v => setLessonForm({...lessonForm, video_type: v})}>
                      <SelectTrigger className="mt-1 rounded-none"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent><SelectItem value="youtube">YouTube</SelectItem><SelectItem value="vimeo">Vimeo</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer border border-amber-200 bg-amber-50 p-2" data-testid="require-video-toggle-new">
                    <input type="checkbox" checked={!!lessonForm.require_video} onChange={e => setLessonForm({...lessonForm, require_video: e.target.checked})} className="mt-0.5" />
                    <span><b>Require video watched</b> — hides the manual complete button; students must watch to at least 90% for the lesson to auto-complete.</span>
                  </label>
                </>
              )}
              <div><Label className="text-sm">Order</Label><Input type="number" value={lessonForm.order} onChange={e => setLessonForm({...lessonForm, order: e.target.value})} className="mt-1 rounded-none" /></div>
              <Button onClick={createLesson} disabled={saving || !lessonForm.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-lesson-btn">{saving ? "Saving..." : "Create Lesson"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Module Dialog */}
        <Dialog open={editModuleDialog} onOpenChange={setEditModuleDialog}>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit Module</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label className="text-sm">Title</Label><Input value={editModuleForm.title} onChange={e => setEditModuleForm({...editModuleForm, title: e.target.value})} className="mt-1 rounded-none" data-testid="edit-module-title" /></div>
              <div><Label className="text-sm">Description</Label><Textarea value={editModuleForm.description} onChange={e => setEditModuleForm({...editModuleForm, description: e.target.value})} className="mt-1 rounded-none" rows={2} data-testid="edit-module-desc" /></div>
              <div><Label className="text-sm">Order</Label><Input type="number" value={editModuleForm.order} onChange={e => setEditModuleForm({...editModuleForm, order: e.target.value})} className="mt-1 rounded-none" /></div>
              <Button onClick={saveEditModule} disabled={saving || !editModuleForm.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-edit-module-btn">{saving ? "Saving..." : "Save Changes"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Lesson Dialog */}
        <Dialog open={editLessonDialog} onOpenChange={setEditLessonDialog}>
          <DialogContent className="sm:max-w-lg rounded-none">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit Lesson</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-2">
              <div><Label className="text-sm">Title</Label><Input value={editLessonForm.title} onChange={e => setEditLessonForm({...editLessonForm, title: e.target.value})} className="mt-1 rounded-none" data-testid="edit-lesson-title" /></div>
              <div>
                <Label className="text-sm">Content Type</Label>
                <Select value={editLessonForm.content_type} onValueChange={v => setEditLessonForm({...editLessonForm, content_type: v})}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="text">Text</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="mixed">Mixed</SelectItem></SelectContent>
                </Select>
              </div>
              {(editLessonForm.content_type === "text" || editLessonForm.content_type === "mixed") && (
                <div><Label className="text-sm">Text Content</Label><Textarea value={editLessonForm.text_content} onChange={e => setEditLessonForm({...editLessonForm, text_content: e.target.value})} className="mt-1 rounded-none" rows={5} data-testid="edit-lesson-text" /></div>
              )}
              {(editLessonForm.content_type === "video" || editLessonForm.content_type === "mixed") && (
                <>
                  <div><Label className="text-sm">Video URL</Label><Input value={editLessonForm.video_url} onChange={e => setEditLessonForm({...editLessonForm, video_url: e.target.value})} className="mt-1 rounded-none" placeholder="YouTube or embed URL" data-testid="edit-lesson-video" /></div>
                  <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer border border-amber-200 bg-amber-50 p-2" data-testid="require-video-toggle-edit">
                    <input type="checkbox" checked={!!editLessonForm.require_video} onChange={e => setEditLessonForm({...editLessonForm, require_video: e.target.checked})} className="mt-0.5" />
                    <span><b>Require video watched</b> — hides the manual complete button; students must watch to at least 90% for the lesson to auto-complete.</span>
                  </label>
                </>
              )}
              <div><Label className="text-sm">Order</Label><Input type="number" value={editLessonForm.order} onChange={e => setEditLessonForm({...editLessonForm, order: e.target.value})} className="mt-1 rounded-none" /></div>
              <Button onClick={saveEditLesson} disabled={saving || !editLessonForm.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-edit-lesson-btn">{saving ? "Saving..." : "Save Changes"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Course Completion Badge Pop-up */}
        <Dialog open={showCompletionBadge} onOpenChange={setShowCompletionBadge}>
          <DialogContent className="rounded-none sm:max-w-md text-center p-0 overflow-y-auto max-h-[95vh]" data-testid="course-completion-dialog">
            <DialogHeader className="sr-only">
              <DialogTitle>Course Completed</DialogTitle>
            </DialogHeader>
            <div className="bg-gradient-to-b from-emerald-600 to-emerald-700 text-white py-6 px-6">
              <div className="text-4xl mb-2">&#127881; &#127942; &#127881;</div>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Course Completed!</h2>
              <p className="text-sm text-emerald-100">Outstanding achievement!</p>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-1">You've completed all modules in</p>
              <p className="text-lg font-bold text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{course?.title}</p>
              <div id="completion-badge" className="bg-white border-2 border-emerald-600 p-6 mb-4 inline-block mx-auto relative">
                {/* Faint SBL seal watermark in the center background */}
                <img
                  src="/sbl_seal.png"
                  alt=""
                  crossOrigin="anonymous"
                  className="absolute inset-0 m-auto w-36 h-36 object-contain opacity-10 pointer-events-none"
                  aria-hidden="true"
                />
                <div className="relative z-10">
                  <div className="w-20 h-20 mx-auto mb-3 bg-emerald-600 text-white rounded-full flex items-center justify-center">
                    <Trophy size={36} weight="fill" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Course Completion</p>
                  <p className="text-sm font-bold text-slate-950 mt-1 max-w-[200px] mx-auto">{course?.title}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{user?.name}</p>
                  {/* SBL seal visible at bottom as verified-by mark */}
                  <div className="flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-emerald-100">
                    <img src="/sbl_seal.png" alt="SBL Seal" crossOrigin="anonymous" className="w-7 h-7 object-contain" />
                    <div className="text-left">
                      <p className="text-[10px] font-bold text-emerald-700 leading-tight">School Builder Lab Africa</p>
                      <p className="text-[8px] text-slate-400 leading-tight">{new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-center flex-wrap">
                <Button onClick={() => {
                  const el = document.getElementById("completion-badge");
                  if (!el) return;
                  import("html2canvas").then(mod => {
                    mod.default(el, { scale: 2, backgroundColor: "#fff", useCORS: true }).then(canvas => {
                      const link = document.createElement("a");
                      link.download = `SBL-Badge-${course?.title?.replace(/\s+/g, "-")}.png`;
                      link.href = canvas.toDataURL("image/png");
                      link.click();
                    });
                  }).catch(() => toast.error("Download failed — try right-clicking the badge to save"));
                }} variant="outline" className="rounded-none gap-2 text-sm" data-testid="download-badge-btn">
                  Download Badge
                </Button>
                <Button onClick={() => setShowCompletionBadge(false)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none px-6" data-testid="close-completion-badge">
                  Continue
                </Button>
              </div>
              {!feedbackSubmitted && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mb-2" data-testid="feedback-pending-notice">
                    Your transcript still needs your <strong>course feedback</strong> (10% of your final score).
                  </p>
                  <Button
                    onClick={() => { setShowCompletionBadge(false); setFeedbackOpen(true); }}
                    variant="outline"
                    className="w-full rounded-none gap-1 text-sm"
                    data-testid="open-feedback-from-badge"
                  >
                    Give Course Feedback now
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <CourseFeedbackForm
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          courseId={id}
          courseTitle={course?.title}
          onSubmitted={() => { setFeedbackSubmitted(true); refreshCompletionStatus(); }}
        />
      </div>
    </Layout>
  );
}
